/**
 * TemplateFilters Component
 * Filter controls for channel, category, and sort
 */

import { memo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  TEMPLATE_CATEGORIES,
  TEMPLATE_CHANNELS,
  TEMPLATE_SORT_OPTIONS,
} from '@/constants/templateConstants';
import type { ITemplateFiltersProps } from '@/@types/messageTemplates';
import type {
  TemplateCategory,
  TemplateChannel,
  TemplateSortOption,
} from '@/constants/templateConstants';

function TemplateFilters({ filters, onFiltersChange, templateCount }: ITemplateFiltersProps) {
  const handleChannelChange = (value: string) => {
    onFiltersChange({
      ...filters,
      channel: value as TemplateChannel | 'all',
    });
  };

  const handleCategoryChange = (value: string) => {
    onFiltersChange({
      ...filters,
      category: value as TemplateCategory | 'all',
    });
  };

  const handleSortChange = (value: string) => {
    onFiltersChange({
      ...filters,
      sort: value as TemplateSortOption,
    });
  };

  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-2 flex-wrap">
        {/* Template count */}
        <span className="text-sm text-muted-foreground">
          {templateCount} template{templateCount !== 1 ? 's' : ''}
        </span>

        {/* Channel filter */}
        <Select value={filters.channel} onValueChange={handleChannelChange}>
          <SelectTrigger className="w-[140px] h-9">
            <SelectValue placeholder="All Channels" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Channels</SelectItem>
            {TEMPLATE_CHANNELS.map(channel => (
              <SelectItem key={channel.value} value={channel.value}>
                {channel.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Category filter */}
        <Select value={filters.category} onValueChange={handleCategoryChange}>
          <SelectTrigger className="w-[150px] h-9">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {TEMPLATE_CATEGORIES.map(category => (
              <SelectItem key={category.value} value={category.value}>
                {category.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Sort */}
        <Select value={filters.sort} onValueChange={handleSortChange}>
          <SelectTrigger className="w-[130px] h-9">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            {TEMPLATE_SORT_OPTIONS.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export const TemplateFiltersMemo = memo(TemplateFilters);
